/**
 * Manages BO comment mutations (status / write-back edits), sorts
 * comments for display, and persists them to UserStore.
 * @module useBoCommentState
 */

import { useEffect, useMemo } from "react";
import { AllCommentsProps } from "../../../../../data-model/busniessOfficeNote";
import { BusniessOfficeReportType } from "../../../../../enums/busniessOfficeReportType";
import { UserGeneralInfoContextKeys } from "../../../../../enums/userGeneralInfoContextKeys";
import { UserStore } from "../../../../../store/UserGeneralInfoStore";

/**
 * Dependencies for {@link useBoCommentState} from Business Office context wiring.
 */
interface UseBoCommentStateParams {
  allCommentsForUI: AllCommentsProps[];
  setAllCommentsForUI: React.Dispatch<React.SetStateAction<AllCommentsProps[]>>;
  orderId: string;
}

/**
 * Encapsulates comment-level mutations, date-sorted display list, and
 * UserStore persistence for business office notes.
 *
 * @param params.allCommentsForUI - Current comments from context
 * @param params.setAllCommentsForUI - Setter from context
 * @param params.orderId - Used as the UserStore key suffix
 * @returns handleBoStatusChange, handleWriteBackCommentChange, sortedCommentsForUI
 */
export const useBoCommentState = ({
  allCommentsForUI,
  setAllCommentsForUI,
  orderId,
}: UseBoCommentStateParams) => {
  const handleBoStatusChange = (data: { id: string; status: string }) => {
    setAllCommentsForUI((prevComments) =>
      prevComments.map((commentGroup) => {
        if (commentGroup.type === BusniessOfficeReportType.FINAL) {
          return {
            ...commentGroup,
            comments: commentGroup.comments.map((comment) =>
              comment.id === data.id
                ? { ...comment, boStatus: data.status }
                : comment,
            ),
          };
        }
        return commentGroup;
      }),
    );
  };

  const handleWriteBackCommentChange = (data: {
    id: string;
    comment: string;
  }) => {
    setAllCommentsForUI((prevComments) =>
      prevComments.map((commentGroup) => {
        if (commentGroup.type === BusniessOfficeReportType.FINAL) {
          return {
            ...commentGroup,
            comments: commentGroup.comments.map((comment) =>
              comment.id === data.id
                ? { ...comment, writeBackComment: data.comment }
                : comment,
            ),
          };
        }
        return commentGroup;
      }),
    );
  };

  const sortedCommentsForUI = useMemo(() => {
    return [...allCommentsForUI].sort((a, b) => {
      const dateA = new Date(a.commentDate);
      const dateB = new Date(b.commentDate);
      return dateB.getTime() - dateA.getTime();
    });
  }, [allCommentsForUI]);

  useEffect(() => {
    if (allCommentsForUI.length > 0) {
      UserStore.setItem(
        `${UserGeneralInfoContextKeys.BO_COMMENTS}_${orderId}`,
        allCommentsForUI,
      );
    }
  }, [allCommentsForUI, orderId]);

  return {
    handleBoStatusChange,
    handleWriteBackCommentChange,
    sortedCommentsForUI,
  };
};
